// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { toast } from 'sonner';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';
import GameScoringSheet from './GameScoringSheet';
import GameScoreCard from '@/components/scoring/GameScoreCard';

const NAVY = '#0a1f44';
const SCORE_FN = 'rle-score-game';

interface StaffScoringViewProps {
  games: any[];
  canScore: boolean;
  periodConfig?: { period_type: string; period_count: number };
  sport?: string;
  onGameUpdated: (game: any) => void;
  /** Re-read every game for the event (a Final fills the next bracket game's slots). */
  onRefreshList?: () => void | Promise<void>;
}


/** Shared score-edge-function caller. Throws on failure so callers can retry. */
export async function callScore(gameId: string, payload: Record<string, any>) {
  const body: Record<string, unknown> = { game_id: gameId, ...payload };
  const { data, error } = await invokeEdgeFunction(SCORE_FN, { body });
  if (error) throw error;
  if (data && data.ok === false) {
    throw new Error(data?.error?.message || 'Could not save score.');
  }
  return {
    game: data?.data?.game,
    periods: data?.data?.periods,
    cumulative: data?.data?.cumulative ?? data?.data?.periods,
    total: data?.data?.total ?? data?.data?.game,
  };
}

function formatDay(dateStr?: string) {
  if (!dateStr) return 'TBD';
  try {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const StaffScoringView: React.FC<StaffScoringViewProps> = ({
  games,
  canScore,
  periodConfig,
  sport,
  onGameUpdated,
  onRefreshList,
}) => {

  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const dates = useMemo(
    () => Array.from(new Set(games.map((g) => g.date).filter(Boolean))).sort(),
    [games],
  );

  const [day, setDay] = useState<string>('');
  useEffect(() => {
    if (day && (day === 'all' || dates.includes(day))) return;
    const t = todayISO();
    setDay(dates.includes(t) ? t : dates[0] || 'all');
  }, [dates]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rank = (g: any) =>
      g.status === 'in_progress' ? 0 : g.status === 'completed' ? 2 : 1;
    return games
      .filter((g) => day === 'all' || !day || g.date === day)
      .filter((g) => {
        if (!q) return true;
        return (
          (g.team_a || '').toLowerCase().includes(q) ||
          (g.team_b || '').toLowerCase().includes(q) ||
          (g.court || '').toLowerCase().includes(q) ||
          (g.division || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        const r = rank(a) - rank(b);
        if (r !== 0) return r;
        return `${a.date || ''} ${a.start || ''} ${a.court || ''}`.localeCompare(
          `${b.date || ''} ${b.start || ''} ${b.court || ''}`,
        );
      });
  }, [games, query, day]);

  const openGame = useMemo(
    () => games.find((g) => g.id === openId) || null,
    [games, openId],
  );

  const remaining = visible.filter((g) => g.status !== 'completed').length;

  // A Final (complete) or Edit (reopen) changes more than this card: the server
  // fills the next bracket game's team slots, so re-read the whole list.
  const handleCardUpdated = (update: any) => {
    onGameUpdated(update);
    void onRefreshList?.();
  };


  return (
    <div className="w-full">
      <div className="relative mb-3">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search team or court"
          className="h-12 pl-9 text-base"
        />
      </div>

      {dates.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {dates.map((d) => (
            <Button
              key={d}
              size="sm"
              variant={day === d ? 'default' : 'outline'}
              className="h-10"
              onClick={() => setDay(d)}
            >
              {d === todayISO() ? 'Today' : formatDay(d)}
            </Button>
          ))}
          <Button
            size="sm"
            variant={day === 'all' ? 'default' : 'outline'}
            className="h-10"
            onClick={() => setDay('all')}
          >
            All days
          </Button>
        </div>
      )}

      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {remaining} game{remaining === 1 ? '' : 's'} still to score
      </p>

      <div className="overflow-hidden rounded-xl border bg-card">
        {visible.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">
            {query ? 'No games match your search.' : 'No games on this day.'}
          </p>
        ) : (
          visible.map((g) => (
            <GameScoreCard
              key={g.id}
              game={g}
              canScore={canScore}
              callScore={callScore}
              onGameUpdated={handleCardUpdated}
              onOpen={() => setOpenId(g.id)}
            />

          ))
        )}
      </div>

      {openGame && (
        <GameScoringSheet
          key={openGame.id}
          game={openGame}
          canScore={canScore}
          periodConfig={periodConfig}
          sport={sport}
          callScore={callScore}
          onGameUpdated={(u) => {
            onGameUpdated(u);
            if (u.status === 'completed') {
              toast.success('Final score saved.');
            }
          }}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  );
};

export default StaffScoringView;
